import { enableMapSet } from 'immer';
import { debounce, isEqual } from 'lodash';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { DeepOmit } from 'ts-essentials';
import { type StoreApi, createStore, useStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { UiNodeContext } from './components/UiNode';
import { getNodeById, getNodePathById, treeNodeUUID } from './utils/utils';

enableMapSet();

// Cool hack for inferring discriminated union types from a map of action creators
// interface Actions {
//   SET_ACTIVE_NODE: { nodeId: string };
//   ADD_NODE: { node: string };
//   REMOVE_NODE: { nodeId: string };
// }

// type Action = {
//   [K in keyof Actions]: { type: K } & Actions[K];
// }[keyof Actions];

export type TreeNodeState = Record<string, unknown>;

export type TreeNodeEditorPanelConfig<NS extends TreeNodeState = TreeNodeState> = {
  panel:
    | React.ReactNode
    | ((props: {
        id: string;
        state: NS;
        setState: TreeNode<NS>['setState'];
        isCurrentActiveNode: boolean;
      }) => React.ReactNode);
};

type TreeNodeSetState<NS extends TreeNodeState = TreeNodeState> = {
  setState: (updater: ((node: TreeNode<NS>['state']) => TreeNode<NS>['state']) | TreeNode<NS>['state']) => void;
  setStateDebounced: (
    updater: ((node: TreeNode<NS>['state']) => TreeNode<NS>['state']) | TreeNode<NS>['state']
  ) => void;
};

export type TreeNode<NS extends TreeNodeState = TreeNodeState> = {
  id: string;
  folder?: {
    title: string;
    icon?: React.ReactNode;
    removable?: boolean;
    onRemove?: () => void;
  };
  config?: {
    /**
     * Alias for the node.
     */
    alias?: string;
    /**
     * When the node is read only, it will not update the state and will not be tracked in the active node.
     */
    readOnly?: boolean;
  };
  nodePath?: string;
  parentId?: string | null | undefined;
  childrenNodes: Map<string, TreeNode<NS>>;
  editorPanelConfig?: TreeNodeEditorPanelConfig<NS>;
  state: NS;
} & TreeNodeSetState<NS>;

type InitStore<NS extends TreeNodeState = TreeNodeState> = {
  activeNode: TreeNode<NS> | null;
  activeNodeRoot: TreeNode<NS> | null;
  lastCreatedNodeId: string | null;
  nodes: Map<string, TreeNode<NS>>;
};

const initStore: InitStore = {
  activeNode: null,
  activeNodeRoot: null,
  lastCreatedNodeId: null,
  nodes: new Map(),
};

export type StoreActions = {
  setActiveNode: <NS extends TreeNodeState = TreeNodeState>(node: TreeNode<NS> | string | null) => void;
  deleteNode: (nodeId: string) => void;
  addNode: <NS extends TreeNodeState = TreeNodeState>(node: TreeNode<NS>) => void;
  updateNode: (
    nodeId: string,
    node: Omit<Partial<TreeNode>, 'childrenNodes' | 'parentId' | 'setState' | 'nodePath' | 'id' | 'state'>
  ) => void;
  updateNodeState: <NS extends TreeNodeState = TreeNodeState>(
    nodeId: string,
    updater: ((node: TreeNode<NS>['state']) => TreeNode<NS>['state']) | TreeNode<NS>['state']
  ) => void;
  reset: () => void;
  setLastCreatedNodeId: (nodeId: string) => void;
};

type Store = InitStore & StoreActions;

const createEditorStore = () => {
  return createStore<Store>()(
    immer((set, get) => {
      return {
        // properties
        ...initStore,
        // actions
        reset() {
          set(initStore);
        },
        setActiveNode(node) {
          set((state) => {
            let actualNode = node;
            if (typeof node === 'string') {
              actualNode = getNodeById(node, state.nodes);
            }
            if (!actualNode) {
              return;
            }

            state.activeNodeRoot = getNodePathById((actualNode as TreeNode)?.id, state.nodes);
            state.activeNode = actualNode as TreeNode | null;
          });
        },
        deleteNode(nodeId) {
          set((state) => {
            const node = getNodeById(nodeId, state.nodes);
            if (!node) {
              return;
            }

            if (node?.parentId) {
              const parentNode = getNodeById(node.parentId, state.nodes);
              if (parentNode) {
                parentNode.childrenNodes.delete(node.id);
              }
            }

            state.nodes.delete(nodeId);
          });
        },
        addNode(node) {
          set((state) => {
            if (node?.parentId) {
              const parentNode = getNodeById(node.parentId, state.nodes);
              if (parentNode) {
                parentNode.childrenNodes.set(node.id, node as TreeNode);
              }
            } else {
              state.nodes.set(node.id, node as TreeNode);
            }
          });
        },
        updateNode(nodeId, node) {
          set((state) => {
            const oldNode = getNodeById(nodeId, state.nodes);
            if (!oldNode) {
              return;
            }

            if (node?.editorPanelConfig) {
              oldNode.editorPanelConfig = node.editorPanelConfig;
            }

            if (node?.folder) {
              oldNode.folder = node.folder;
            }
          });
        },
        updateNodeState: <NS extends TreeNodeState = TreeNodeState>(
          nodeId: string,
          updater: ((n: TreeNode<NS>['state']) => TreeNode<NS>['state']) | TreeNode<NS>['state']
        ) => {
          set((state) => {
            const node = getNodeById(nodeId, state.nodes) as TreeNode<NS> | null;
            if (!node) {
              return;
            }

            let newState = {} as TreeNode<NS>['state'];
            if (typeof updater === 'function') {
              newState = updater(node?.state as TreeNode<NS>['state']);
            } else {
              newState = updater;
            }

            const newNode = { ...node, state: newState } as TreeNode<NS>;
            const parentNode = node?.parentId ? getNodeById(node.parentId, state.nodes) : null;

            if (parentNode) {
              parentNode.childrenNodes.set(nodeId, newNode as TreeNode);
            } else {
              state.nodes.set(nodeId, newNode as TreeNode);
            }
          });
        },
        setLastCreatedNodeId(nodeId) {
          set((state) => {
            state.lastCreatedNodeId = nodeId;
          });
        },
        // end actions
      };
    })
  );
};

type EditorContextType = {
  store: StoreApi<Store>;
  config?: {
    readOnly?: boolean;
  };
};

const EditorContext = createContext<EditorContextType | null>(null);

/**
 * EditorProvider is a context provider that creates and provides an editor store.
 */
export const EditorProvider = ({
  children,
  config,
}: { children: React.ReactNode; config?: EditorContextType['config'] }) => {
  const storeRef = useRef<ReturnType<typeof createEditorStore>>(createEditorStore());

  useEffect(() => {
    return () => {
      storeRef.current?.getState().reset();
    };
  }, []);

  return (
    <EditorContext.Provider
      value={{
        store: storeRef.current,
        config: config,
      }}
    >
      {children}
    </EditorContext.Provider>
  );
};

export const useEditorContext = () => {
  return useContext(EditorContext);
};

export function useEditorStoreApi() {
  const ctx = useContext(EditorContext);
  if (!ctx?.store) {
    throw new Error('Missing EditorProvider');
  }
  return ctx.store;
}

/**
 * Hook to access the editor store created by the EditorProvider.
 */
export function useEditorStore<U>(selector: (store: Store) => U): U {
  const store = useEditorStoreApi();

  return useStore(store, selector);
}

export const useNodeState = <NS extends TreeNodeState>(nodeId: string | null | undefined) => {
  const nodes = useEditorStore((state) => state.nodes);

  const node = useMemo(() => {
    if (!nodeId) {
      return null;
    }

    return getNodeById(nodeId, nodes);
  }, [nodeId, nodes]);

  const [state, setState] = useState<NS>(node?.state as NS);

  useEffect(() => {
    if (!node) {
      return;
    }

    if (isEqual(state, node.state)) {
      return;
    }

    setState(node.state as NS);
  }, [node]);

  return state;
};

/**
 * Hook to get the active node and active node root.
 */
export const useEditorActiveNode = <NS extends TreeNodeState>() => {
  const nodes = useEditorStore((state) => state.nodes);
  const activeNode = useEditorStore((state) => state.activeNode) as TreeNode<NS> | null;
  const activeNodeRoot = useEditorStore((state) => state.activeNodeRoot) as TreeNode<NS> | null;

  const activeNodeMemo = useMemo(() => {
    if (!activeNode) {
      return null;
    }

    return getNodeById(activeNode.id, nodes);
  }, [activeNode, nodes]);

  const activeNodeRootMemo = useMemo(() => {
    if (!activeNodeRoot) {
      return null;
    }

    return getNodeById(activeNodeRoot.id, nodes);
  }, [activeNodeRoot, nodes]);

  const setActiveNodeFromStore = useEditorStore((state) => state.setActiveNode<NS>);
  const setActiveNode = useCallback(
    (node: Parameters<typeof setActiveNodeFromStore>[0]) => {
      if (typeof node === 'string' && node === activeNode?.id) {
        return;
      }
      if (typeof node === 'object' && node?.id === activeNode?.id) {
        return;
      }

      setActiveNodeFromStore(node);
    },
    [setActiveNodeFromStore, activeNode]
  );

  return {
    activeNode: activeNodeMemo,
    activeNodeRoot: activeNodeRootMemo,
    setActiveNode,
  };
};

type UseCreateNode<NS extends TreeNodeState> = {
  node: DeepOmit<Partial<TreeNode<NS>>, { id: never; setState: never; childrenNodes: never }>;
};

/**
 * Hook to create a new node in the editor store.
 */
export const useCreateNode = <NS extends TreeNodeState>({ node }: UseCreateNode<NS>) => {
  const parentCtx = useContext(UiNodeContext);

  const store = useEditorStoreApi();
  const id = useRef(treeNodeUUID());

  const value = useEditorStore((state) => getNodeById<NS>(id.current, state.nodes));
  const nodeStateUpdater = useEditorStore((s) => s.updateNodeState);

  const updateNodeConfig = useEditorStore((s) => s.updateNode);

  /**
   * debounce the updater function
   */
  const updaterFn = useCallback(
    debounce(
      (updater: Parameters<typeof nodeStateUpdater<NS>>[1]) => {
        return nodeStateUpdater(id.current, updater);
      },
      250,
      { leading: false, trailing: true }
    ),
    []
  );

  /**
   * create the node when the component mounts
   */
  useEffect(() => {
    const state = store.getState();
    state.addNode({
      id: id.current,
      folder: node?.folder,
      config: node?.config,
      state: node?.state ?? ({} as NS),
      editorPanelConfig: node?.editorPanelConfig,
      childrenNodes: new Map(),
      setState(updater) {
        return nodeStateUpdater(id.current, updater);
      },
      setStateDebounced: updaterFn,

      nodePath: [parentCtx?.node?.nodePath, id.current].filter(Boolean).join(','),
      parentId: parentCtx?.node?.id,
    });

    state.setLastCreatedNodeId(id.current);

    return () => {
      state.deleteNode(id.current);
    };
  }, [id.current]);

  /**
   * update the editorPanelConfig when it changes
   */
  useEffect(() => {
    if (!id?.current || !node?.editorPanelConfig) {
      return;
    }
    updateNodeConfig(id.current, {
      // @ts-ignore
      editorPanelConfig: node?.editorPanelConfig,
    });
  }, [node?.editorPanelConfig]);

  /**
   * update the folder when it changes
   */
  useEffect(() => {
    if (!id?.current || !node?.folder) {
      return;
    }

    updateNodeConfig(id.current, {
      folder: node?.folder,
    });
  }, [node?.folder]);

  /**
   * update the config when it changes
   */
  useEffect(() => {
    if (!id?.current || !node?.config) {
      return;
    }

    updateNodeConfig(id.current, {
      config: node?.config,
    });
  }, [node?.config]);

  return [value, updaterFn] as const;
};

/**
 * focus the last known created node
 */
export const useFocusLastCreatedNode = () => {
  const store = useEditorStoreApi();

  return useCallback(() => {
    const state = store.getState();

    state.setActiveNode(state.lastCreatedNodeId);
  }, [store]);
};
